/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
export default {
  parts: [
    'container',
    'content',
    'topFooter',
    'mainContainer',
    'siteInformation',
    'brandSection',
    'linksSection',
    'accordionWrapper',
    'accordionSection',
    'accordionItem',
    'accordionButton',
    'accordionHeading',
    'accordionIcon',
    'accordionPanel',
    'listItem',
    'listItemTitle',
    'listItemContent',
    'bottomFooterWrapper',
    'bottomFooter',
    'bottomCopyright',
    'bottomIcons'
  ],
  baseStyle: {
    container: {
      width: 'full',
      background: 'brandColors.greyscale',
    },
    content: {
      maxWidth: 'container.xxl',
      marginLeft: 'auto',
      marginRight: 'auto',
      color: 'white',
      pb: {base: 12.5, md: 16},
      paddingX: [5, 10, 15, 20], // 20px, 40px, 60px, 80px
      gap: {base: 12.5, md: 16}, // 50px, 64px
      sx: {
        ' .footer-copyright': {
          fontSize: 'xs',
          color: {base: 'whiteAlpha.500', md: 'whiteAlpha.700'},
          fontWeight: 'normal',
        },
      },
    },
    topFooter: {
      display: 'flex',
      paddingY: 12, // 48px
      paddingX: 0,
      justifyContent: {base: 'flex-start', md: 'space-between'},
      alignItems: 'flex-start',
      alignContent: 'flex-start',
      gap: {base: 2, md: 3}, // 8px, 12px
      alignSelf: 'stretch',
      flexWrap: 'wrap',
      borderBottom: '1px solid',
      borderColor: 'whiteAlpha.350',
      color: 'white',
      fontSize: {base: 'md', md: 'lg'},
      fontStyle: 'normal',
      fontWeight: 'medium',
      lineHeight: '1.2', /* 21.6px */
      sx: {
        ' sup': {
          color: 'whiteAlpha.650',
          fontWeight: 'light',
          letterSpacing: {base: '-0.08rem', md: 0},
        },
      },
    },
    mainContainer: {
      direction: {base: 'column', lg: 'row'},
      justify: 'space-between',
      align: {base: 'flex-start', lg: 'stretch'},
      width: 'full',
      gap: {base: 6, lg: 0},
    },
    siteInformation: {
      flexShrink: 0,
      flexGrow: 0,
      flexBasis: {base: 'full', lg: '36%'},
    },
    brandSection: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      alignSelf: 'stretch',
      sx: {
        ' .footer-brand': {
          display: 'flex',
          flexDirection: 'column',
          gap: {base: 6, md: 16},
          width: '100%',
        },
        ' .footer-logo': {
          width: 50,
          height: 11,
          color: 'white',
          aspectRatio: '200/43',
        },
        ' .footer-info': {
          color: 'whiteAlpha.800',
          fontSize: 'sm',
          lineHeight: '1.4',
          fontWeight: 'normal',
        },
        ' .footer-copyright': {
          display: {base: 'none', md: 'block'},
        },
        ' p': {
          marginBottom: 4,
        },
        ' p:last-child': {
          marginBottom: 0,
        },
      },
    },
    linksSection: {
      gap: {base: 0, lg: 12},
      flexShrink: 0,
      flexGrow: 0,
      width: {base: 'full', lg: '48%'},
      flexBasis: {base: 'full', lg: '48%'},
      flexDirection: {base: 'column', lg: 'row'},
      sx: {
        ' > *': {
          width: {base: 'full', lg: '33.3333%'},
          flexBasis: {base: 'full', lg: '33.3333%'},
        },
      },
    },
    accordionWrapper: {
      gap: {base: 0, lg: 20}, // 0px, 80px
      alignItems: 'start',
    },
    accordionSection: {
      width: 'full',
    },
    accordionItem: {
      border: 'none',
    },
    accordionButton: {
      display: 'flex',
      height: '11',
      paddingY: '1.5',
      paddingX: '0',
      alignItems: 'center',
      gap: '5',
      alignSelf: 'stretch',
      background: 'transparent',
      border: 'none',
      _hover: {
        background: 'transparent',
      },
      _focus: {
        boxShadow: 'none',
      },
      _expanded: {
        '& svg': {
          transform: 'rotate(180deg)',
        },
      },
    },
    accordionHeading: {
      flex: '1 0 0',
      color: 'white',
      fontSize: 'md',
      fontWeight: 'medium',
      textAlign: 'left',
      lineHeight: '1.2', // 19.2px
    },
    accordionIcon: {
      width: '11',
      color: 'whiteAlpha.400',
      strokeWidth: '1.5',
      transition: 'transform 0.2s ease-in-out',
    },
    accordionPanel: {
      px: 0,
      pb: 4,
    },
    listItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: 5, // 20px
    },
    listItemTitle: {
      color: 'white',
      fontSize: 'md',
      fontWeight: 'medium',
    },
    listItemContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4, // 16px
      alignSelf: 'stretch',
      opacity: 0.7,
      sx: {
        '& a, & span, & div': {
          color: 'white',
          fontSize: 'sm',
          textDecoration: 'none',
          lineHeight: '1.2',
        },
      },
    },
    bottomFooterWrapper: {
      pt: {base: 0, md: 12},
      alignItems: 'start',
      gap: 6, // 24px
      borderTop: '1px solid',
      borderColor: {base: 'transparent', md: 'whiteAlpha.350'},
    },
    bottomFooter: {
      fontSize: 'xs',
      color: 'whiteAlpha.700',
      fontWeight: 'normal',
      width: 'full',
    },
    bottomCopyrightWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: {base: 'flex-start', md: 'center'},
      gap: 6,
      width: 'full',
      flexWrap: 'wrap',
      flexDirection: {base: 'column', md: 'row'},
    },
    bottomCopyright: {
      fontSize: 'xs',
      color: 'whiteAlpha.700',
      fontWeight: 'normal',
    },
    bottomIcons: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 2, // 8px
      flexWrap: 'wrap',
      sx: {
        ' img': {
          maxWidth: {base: 10.25, md: 12.75},
        },
      },
    },
  },
};
